/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Envelope parser. Accepts raw bytes or a JSON string, produces a
 * Zod-validated `EnvelopeOuter`, throws typed errors from
 * `errors.ts` on any rejection path. Fuzz harness target — every input
 * must result in either a successful parse or an `EnvelopeParseError`
 * subclass.
 *
 * The redesign doc's "one schema version, one parser, frozen forever"
 * rule is encoded here: this parser handles ENVELOPE_VERSION = 1 only.
 * A v2 parser (when needed) lives in a separate file; `parseEnvelope`
 * dispatches via the version pre-check.
 */

import { ZodError } from 'zod';
import {
  ENVELOPE_VERSION,
  EnvelopeOuterSchema,
  type EnvelopeOuter,
} from './schema.js';
import {
  EnvelopeParseError,
  EnvelopeSchemaError,
  InvalidBase64Error,
  UnsupportedVersionError,
} from './errors.js';

const BASE64_ZOD_MARKER = 'invalid base64nopad encoding';

const decodeUtf8 = (bytes: Uint8Array): string => {
  try {
    // `fatal: true` rejects malformed UTF-8 sequences instead of mapping
    // them to U+FFFD — a parser cares about exact round-trip fidelity.
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch (cause) {
    throw new EnvelopeParseError('input is not valid UTF-8', { cause });
  }
};

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch (cause) {
    throw new EnvelopeParseError('input is not valid JSON', { cause });
  }
};

const isBase64Issue = (err: ZodError): boolean =>
  err.issues.some(issue => issue.message === BASE64_ZOD_MARKER);

/**
 * Parse and validate an envelope from raw bytes or a JSON string.
 *
 * @param input - either bytes (UTF-8 JSON) or the JSON string itself
 * @returns a Zod-validated `EnvelopeOuter` with byte fields as `Uint8Array`
 * @throws {EnvelopeParseError} for malformed input, JSON, or UTF-8
 * @throws {UnsupportedVersionError} when `version` is present but not 1
 * @throws {InvalidBase64Error} when a base64-encoded field can't be decoded
 * @throws {EnvelopeSchemaError} for any other Zod validation failure
 */
export const parseEnvelope = (input: Uint8Array | string): EnvelopeOuter => {
  const text = typeof input === 'string' ? input : decodeUtf8(input);
  const json = parseJson(text);

  // Pre-check the version field so a wrong version surfaces as
  // UnsupportedVersionError rather than a generic schema error.
  if (typeof json !== 'object' || json === null || Array.isArray(json)) {
    throw new EnvelopeSchemaError('envelope must be a JSON object');
  }
  const observedVersion = (json as Record<string, unknown>).version;
  if (observedVersion !== ENVELOPE_VERSION) {
    // Only treat as UnsupportedVersionError when the field is *present*
    // but wrong. If absent / null, fall through to the schema check so
    // the user sees a meaningful "missing version" error.
    if (observedVersion !== undefined) {
      throw new UnsupportedVersionError(observedVersion);
    }
  }

  try {
    return EnvelopeOuterSchema.parse(json);
  } catch (err) {
    if (err instanceof ZodError) {
      if (isBase64Issue(err)) {
        throw new InvalidBase64Error(err.message, { cause: err });
      }
      throw new EnvelopeSchemaError(err.message, { cause: err });
    }
    // Unknown error from Zod (shouldn't happen). Wrap defensively so the
    // fuzz harness's "every error is typed" invariant holds.
    throw new EnvelopeParseError('unexpected parse failure', { cause: err });
  }
};
